import * as React from "react";
import { classNames } from "uitk-react-utils";
import { ClientWizardStateStore } from "src/stores/wizard/ClientWizardStateStore";
import { observer, inject } from "mobx-react";
import { UitkCardAloha } from "uitk-react-cards";
import { LobFormWithTabs } from "src/components/shared/StorefrontWizard/LobFormWithTabs/LobFormWithTabs.tsx";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import {
  UitkLayoutConditionalGridColumnRow,
  UitkLayoutGrid,
  UitkLayoutConditionalGridSpan,
  UitkLayoutGridItem,
} from "uitk-react-layout-grid";

export interface TabbedWizardCardProps {
  context?: ExtendedContextStore;
  wizardState: ClientWizardStateStore;
  wizardFormChildren: React.ReactElement[];
  wrappingClassNames?: string;
  tabGroupClassNames?: string;
  wizardFormClassNames?: string;
  wizardRegionType: WizardRegionType;
  trackOnChangeTab?: (tabName: string) => void;
  showTabsIcon?: boolean;
}

export enum WizardRegionType {
  BEX,
  COMET,
  MESO,
  TAAP,
}

const getPaddingBasedOnLobSize = (childrenSize: number) => ({
  "all-y-padding-six": childrenSize === 1,
  "all-b-padding-six": childrenSize > 1,
});
const getAlohaCardClassName = (wizardRegionType: WizardRegionType) => (wizardFormChildren: React.ReactElement[]) => {
  const wizardsWithElevation: WizardRegionType[] = [WizardRegionType.BEX, WizardRegionType.MESO, WizardRegionType.TAAP];
  return classNames(
    "all-cell-1-1",
    getPaddingBasedOnLobSize(wizardFormChildren.length),
    { "elevation-4": wizardsWithElevation.includes(wizardRegionType) },
    { multiLobCard: wizardRegionType === WizardRegionType.BEX && wizardFormChildren.length > 1 }
  );
};

/**
 * Returns a tabbed wizard within a uitk-card
 * Padding/styling of elements within card are handed down by parent region
 * Assumes any non-wizard form children are handled by parent region
 */
export const TabbedWizardCard: React.FunctionComponent<TabbedWizardCardProps> = inject(
  "wizardState",
  "context"
)(
  observer(
    ({
      context,
      wizardState,
      wizardFormChildren,
      wrappingClassNames = "",
      tabGroupClassNames = "",
      wizardFormClassNames = "",
      wizardRegionType,
      trackOnChangeTab,
      showTabsIcon = true,
    }: TabbedWizardCardProps) => {
      if (!wizardState) {
        return null;
      }
      const { halfWizard } = wizardState.globalWizardState.config;
      const mainGridColumns: UitkLayoutConditionalGridColumnRow = { small: 2, large: 12 };
      const cardColSpan: UitkLayoutConditionalGridSpan = { small: 2, large: halfWizard ? 6 : 12 };
      const isSingleLOB = wizardFormChildren?.length === 1;

      return (
        <UitkLayoutGrid columns={mainGridColumns} className={`wizardCard ${wrappingClassNames}`}>
          <UitkLayoutGridItem colSpan={cardColSpan}>
            <UitkCardAloha className={getAlohaCardClassName(wizardRegionType)(wizardFormChildren)}>
              <div>
                {isSingleLOB ? (
                  wizardFormChildren
                ) : (
                  <LobFormWithTabs
                    context={context}
                    lobForms={wizardFormChildren}
                    wizardFormClassNames={wizardFormClassNames}
                    tabGroupClassNames={tabGroupClassNames}
                    trackOnChangeTab={trackOnChangeTab}
                    showTabsIcon={showTabsIcon}
                  />
                )}
              </div>
            </UitkCardAloha>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
      );
    }
  )
);
